import React, { useCallback, useMemo, useState } from 'react';

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { blue, green, red } from '@mui/material/colors';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';

import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

import * as AccommodationService from '~/services/AccommodationService';

import { useCatalogItemFilters } from '../../../hooks/useCatalogItemFilters';
import { useCatalogItemMutations } from '../../../hooks/useCatalogItemMutations';
import { BaseCatalogItem, CatalogItemConfig } from '../../../types/catalogItem';
import { InlineCheckboxEditCell, InlineNumberEditCell, InlineTextFieldEditCell } from '../../../utils/datagrid';

import ManageCatalogGroupsModal from './ManageCatalogGroupsModal';
import ManageSupplierNamesModal from './ManageSupplierNamesModal';
import MergeCatalogItemModal from './MergeCatalogItemModal';

const PAGE_SIZE = 50;
const NEW_ROW_CODE_PREFIX = 'new-';

interface CatalogGroupOption {
  id: string;
  name: string;
  code: string;
}

type CatalogItemRowData<TItem extends BaseCatalogItem> = TItem & {
  isModified?: boolean;
  pendingChanges?: Partial<TItem>;
  isNew?: boolean;
};

type NewItemChanges<TUpdatePayload> = TUpdatePayload & {
  code?: string;
  name?: string;
  aliases?: string[];
  catalogGroupId?: string;
  displayOrder?: number;
  featured?: boolean;
  icon?: AccommodationService.Icon | null;
};

interface CatalogItemResultsContainerProps<
  TItem extends BaseCatalogItem,
  TUpdatePayload,
  TCreatePayload,
  TApprovePayload,
  TMergePayload,
> {
  config: CatalogItemConfig<TItem, TUpdatePayload, TCreatePayload, TApprovePayload, TMergePayload>;
}

export default function CatalogItemResultsContainer<
  TItem extends BaseCatalogItem,
  TUpdatePayload,
  TCreatePayload,
  TApprovePayload,
  TMergePayload,
>({ config }: CatalogItemResultsContainerProps<TItem, TUpdatePayload, TCreatePayload, TApprovePayload, TMergePayload>) {
  const { enqueueSnackbar } = useSnackbar();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const { filters, setSearchName, setFilterCatalogGroupId, setFilterStatus, clearFilters } = useCatalogItemFilters();
  const [modifiedRows, setModifiedRows] = useState<Record<string, NewItemChanges<TUpdatePayload>>>({});
  const [isManageGroupsModalOpen, setIsManageGroupsModalOpen] = useState(false);
  const [deletingCodes, setDeletingCodes] = useState<Set<string>>(new Set());
  const [localRows, setLocalRows] = useState<Array<CatalogItemRowData<TItem>>>([]);
  const [approvingCodes, setApprovingCodes] = useState<Set<string>>(new Set());
  const [discardingCodes, setDiscardingCodes] = useState<Set<string>>(new Set());
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [selectedDraftForMerge, setSelectedDraftForMerge] = useState<TItem | null>(null);
  const [isManageSupplierNamesModalOpen, setIsManageSupplierNamesModalOpen] = useState(false);
  const [selectedItemForSupplierNames, setSelectedItemForSupplierNames] = useState<TItem | null>(null);

  const showConfirmDialog = useConfirmDialog();

  // Fetch catalog groups for dropdown
  const { data: catalogGroupsData } = useQuery({
    queryKey: ['accommodation', 'catalog-groups'],
    queryFn: async () => {
      const response = await AccommodationService.listCatalogGroups({ limit: '1000', page: '1' });
      return response.result;
    },
  });

  const catalogGroups = catalogGroupsData?.groups ?? [];

  // Fetch icons for dropdown
  const { data: iconsData } = useQuery({
    queryKey: ['accommodation', 'icons'],
    queryFn: async () => {
      const response = await AccommodationService.listIcons();
      return response.result;
    },
  });

  const icons = iconsData?.icons ?? [];

  // Fetch items
  const {
    data: itemsData,
    isLoading,
    isFetching,
    isPlaceholderData,
    error,
  } = useQuery({
    queryKey: [
      'accommodation',
      config.queryKeyPrefix,
      {
        page: paginationModel.page + 1,
        searchName: filters.searchName,
        filterCatalogGroupId: filters.filterCatalogGroupId,
        filterStatus: filters.filterStatus,
      },
    ],
    queryFn: async () => {
      const response = await config.listItems({
        name: filters.searchName || undefined,
        catalogGroupId: filters.filterCatalogGroupId,
        status: filters.filterStatus,
        limit: String(PAGE_SIZE),
        page: String(paginationModel.page + 1),
      });
      return response.result;
    },
    placeholderData: keepPreviousData,
  });

  const items = itemsData?.items ?? [];
  const totalCount = itemsData?.total ?? 0;

  // Mutations
  const { updateMutation, deleteMutation, createMutation, approveMutation, discardMutation, mergeMutation } =
    useCatalogItemMutations({
      config,
      onUpdateSuccess: (_data, variables) => {
        setModifiedRows((prev) => {
          const updated = { ...prev };
          delete updated[variables.code];
          return updated;
        });
      },
      onDeleteSuccess: (_data, code) => {
        setDeletingCodes((prev) => {
          const updated = new Set(prev);
          updated.delete(code);
          return updated;
        });
      },
      onDeleteError: (_error, code) => {
        setDeletingCodes((prev) => {
          const updated = new Set(prev);
          updated.delete(code);
          return updated;
        });
      },
      onCreateSuccess: (_data, variables) => {
        setLocalRows((prev) => prev.filter((r) => r.code !== (variables as any).tempCode));
        setModifiedRows((prev) => {
          const updated = { ...prev };
          delete updated[(variables as any).tempCode];
          return updated;
        });
      },
      onApproveSuccess: (_data, variables) => {
        setApprovingCodes((prev) => {
          const updated = new Set(prev);
          updated.delete(variables.code);
          return updated;
        });
      },
      onApproveError: (_error, variables) => {
        setApprovingCodes((prev) => {
          const updated = new Set(prev);
          updated.delete(variables.code);
          return updated;
        });
      },
      onDiscardSuccess: (_data, code) => {
        setDiscardingCodes((prev) => {
          const updated = new Set(prev);
          updated.delete(code);
          return updated;
        });
      },
      onDiscardError: (_error, code) => {
        setDiscardingCodes((prev) => {
          const updated = new Set(prev);
          updated.delete(code);
          return updated;
        });
      },
      onMergeSuccess: () => {
        setIsMergeModalOpen(false);
        setSelectedDraftForMerge(null);
      },
    });

  const handleAddNew = useCallback(() => {
    const tempCode = `${NEW_ROW_CODE_PREFIX}${Date.now()}`;
    const newRow: CatalogItemRowData<TItem> = {
      code: tempCode,
      name: '',
      description: null,
      aliases: [],
      catalogGroup: undefined,
      icon: null,
      featured: false,
      displayOrder: undefined,
      isNew: true,
      status: 'approved',
    } as CatalogItemRowData<TItem>;
    setLocalRows((prev) => [newRow, ...prev]);
    setModifiedRows((prev) => ({
      ...prev,
      [tempCode]: { code: '' } as NewItemChanges<TUpdatePayload>,
    }));
  }, []);

  const handleSave = useCallback(
    (item: CatalogItemRowData<TItem>) => {
      const changes = modifiedRows[item.code];
      if (!changes) return;

      if (item.isNew || item.code.startsWith(NEW_ROW_CODE_PREFIX)) {
        // Create new item
        if (!changes.code || !(changes as any).catalogGroupId) {
          enqueueSnackbar(`Code and Group are required for new ${config.itemNamePlural.toLowerCase()}`, {
            variant: 'error',
          });
          return;
        }
        createMutation.mutate({
          tempCode: item.code,
          code: changes.code,
          name: changes.name ?? '',
          aliases: changes.aliases,
          catalogGroupId: changes.catalogGroupId,
          displayOrder: changes.displayOrder,
          featured: changes.featured,
          icon: changes.icon,
        } as unknown as TCreatePayload & { tempCode: string });
      } else {
        // Update existing item
        updateMutation.mutate({
          code: item.code,
          data: changes as unknown as TUpdatePayload,
        });
      }
    },
    [modifiedRows, updateMutation, createMutation, enqueueSnackbar, config],
  );

  const handleClearChanges = useCallback((code: string) => {
    const isNewRow = code.startsWith(NEW_ROW_CODE_PREFIX);

    if (isNewRow) {
      setLocalRows((prev) => prev.filter((r) => r.code !== code));
    }
    setModifiedRows((prev) => {
      const updated = { ...prev };
      delete updated[code];
      return updated;
    });
  }, []);

  const handleDeleteClick = useCallback(
    async (item: CatalogItemRowData<TItem>) => {
      const isNewRow = item.code.startsWith(NEW_ROW_CODE_PREFIX);

      if (isNewRow) {
        setLocalRows((prev) => prev.filter((r) => r.code !== item.code));
        setModifiedRows((prev) => {
          const updated = { ...prev };
          delete updated[item.code];
          return updated;
        });
      } else {
        const confirmed = await showConfirmDialog({
          title: `Delete ${config.itemName}`,
          description: `Are you sure you want to delete the ${config.itemName.toLowerCase()} "${item.name}" (${
            item.code
          })? This action cannot be undone.`,
        });
        if (confirmed) {
          setDeletingCodes((prev) => new Set(prev).add(item.code));
          deleteMutation.mutate(item.code);
        }
      }
    },
    [showConfirmDialog, deleteMutation, config],
  );

  const handleApprove = useCallback(
    (item: CatalogItemRowData<TItem>) => {
      const pendingChanges = modifiedRows[item.code];
      const catalogGroupId = (pendingChanges as any)?.catalogGroupId ?? item.catalogGroup?.id;
      const name = (pendingChanges as any)?.name ?? item.name;
      const aliases = (pendingChanges as any)?.aliases ?? item.aliases;

      if (!catalogGroupId) {
        enqueueSnackbar(`Cannot approve: ${config.itemName.toLowerCase()} must have a catalog group assigned`, {
          variant: 'error',
        });
        return;
      }
      setApprovingCodes((prev) => new Set(prev).add(item.code));
      approveMutation.mutate({
        code: item.code,
        data: {
          catalogGroupId,
          name,
          aliases,
        } as TApprovePayload,
      });
    },
    [modifiedRows, approveMutation, enqueueSnackbar, config],
  );

  const handleDiscard = useCallback(
    (code: string) => {
      setDiscardingCodes((prev) => new Set(prev).add(code));
      discardMutation.mutate(code);
    },
    [discardMutation],
  );

  const handleOpenMergeModal = useCallback((item: TItem) => {
    setSelectedDraftForMerge(item);
    setIsMergeModalOpen(true);
  }, []);

  const handleMerge = useCallback(
    (draftItemCode: string, existingItemCode: string) => {
      // Merge payload structure: sourceFacilityCode/targetFacilityCode or sourceAmenityCode/targetAmenityCode
      const sourceKey = config.type === 'facility' ? 'sourceFacilityCode' : 'sourceAmenityCode';
      const targetKey = config.type === 'facility' ? 'targetFacilityCode' : 'targetAmenityCode';
      mergeMutation.mutate({
        [sourceKey]: draftItemCode,
        [targetKey]: existingItemCode,
      } as TMergePayload);
    },
    [mergeMutation, config],
  );

  const handleOpenManageSupplierNames = useCallback((item: TItem) => {
    setSelectedItemForSupplierNames(item);
    setIsManageSupplierNamesModalOpen(true);
  }, []);

  const handleSaveSupplierNames = useCallback(
    async (aliases: string[]) => {
      if (!selectedItemForSupplierNames) return;

      const changes = modifiedRows[selectedItemForSupplierNames.code];
      const updatePayload: TUpdatePayload = {
        ...changes,
        aliases,
      } as TUpdatePayload;

      setModifiedRows((prev) => ({
        ...prev,
        [selectedItemForSupplierNames.code]: updatePayload as NewItemChanges<TUpdatePayload>,
      }));

      await updateMutation.mutateAsync({
        code: selectedItemForSupplierNames.code,
        data: updatePayload,
      });
    },
    [selectedItemForSupplierNames, modifiedRows, updateMutation],
  );

  const handleSearchSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handlePaginationModelChange = useCallback((newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
  }, []);

  const columns: Array<GridColDef<CatalogItemRowData<TItem>>> = useMemo(
    () => [
      {
        field: 'code',
        headerName: 'Code',
        width: 220,
        renderCell: (params: GridRenderCellParams<CatalogItemRowData<TItem>>) => {
          const isNewRow = params.row.code.startsWith(NEW_ROW_CODE_PREFIX);
          if (isNewRow) {
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <InlineTextFieldEditCell
                  value={modifiedRows[params.row.code]?.code ?? ''}
                  onChange={(value) => {
                    setModifiedRows((prev) => ({
                      ...prev,
                      [params.row.code]: { ...prev[params.row.code], code: value } as NewItemChanges<TUpdatePayload>,
                    }));
                  }}
                  placeholder={`Enter ${config.itemName.toLowerCase()} code`}
                />
              </Box>
            );
          }
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Typography variant="body2">{params.value}</Typography>
            </Box>
          );
        },
      },
      {
        field: 'name',
        headerName: 'Display Name',
        width: 220,
        renderCell: (params: GridRenderCellParams<CatalogItemRowData<TItem>>) => {
          const currentValue = modifiedRows[params.row.code]?.name ?? params.value;
          return (
            <InlineTextFieldEditCell
              value={currentValue}
              onChange={(value) => {
                setModifiedRows((prev) => ({
                  ...prev,
                  [params.row.code]: { ...prev[params.row.code], name: value } as NewItemChanges<TUpdatePayload>,
                }));
              }}
            />
          );
        },
      },
      {
        field: 'catalogGroup',
        headerName: 'Group',
        width: 200,
        valueGetter: (_value, row) => row.catalogGroup,
        renderCell: (params: GridRenderCellParams<CatalogItemRowData<TItem>>) => {
          const pendingGroupId = (modifiedRows[params.row.code] as any)?.catalogGroupId;
          const currentGroup = pendingGroupId ? catalogGroups.find((g) => g.id === pendingGroupId) : params.value;

          const options: Array<CatalogGroupOption> = catalogGroups.map((group) => ({
            id: group.id,
            name: group.name,
            code: group.code,
          }));

          const selectedOption = options.find((opt) => opt.id === currentGroup?.id) ?? null;

          return (
            <Autocomplete
              size="small"
              options={options}
              value={selectedOption}
              onChange={(_event, newValue: CatalogGroupOption | null) => {
                setModifiedRows((prev) => ({
                  ...prev,
                  [params.row.code]: {
                    ...prev[params.row.code],
                    catalogGroupId: newValue?.id,
                  } as NewItemChanges<TUpdatePayload>,
                }));
              }}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(inputParams) => <TextField {...inputParams} variant="outlined" />}
              fullWidth
              disableClearable
              sx={{ minWidth: 180 }}
            />
          );
        },
      },
      {
        field: 'displayOrder',
        headerName: 'Rank',
        width: 80,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params: GridRenderCellParams<CatalogItemRowData<TItem>>) => {
          const currentValue = modifiedRows[params.row.code]?.displayOrder ?? params.value;
          return (
            <InlineNumberEditCell
              value={currentValue}
              onChange={(value) => {
                setModifiedRows((prev) => ({
                  ...prev,
                  [params.row.code]: {
                    ...prev[params.row.code],
                    displayOrder: value,
                  } as NewItemChanges<TUpdatePayload>,
                }));
              }}
            />
          );
        },
      },
      {
        field: 'featured',
        headerName: 'Featured',
        width: 80,
        renderCell: (params: GridRenderCellParams<CatalogItemRowData<TItem>>) => {
          const currentValue = modifiedRows[params.row.code]?.featured ?? params.value ?? false;
          return (
            <InlineCheckboxEditCell
              value={currentValue}
              onChange={(value) => {
                setModifiedRows((prev) => ({
                  ...prev,
                  [params.row.code]: { ...prev[params.row.code], featured: value } as NewItemChanges<TUpdatePayload>,
                }));
              }}
            />
          );
        },
      },
      {
        field: 'catalogGroupIcon',
        headerName: 'Group Icon',
        width: 120,
        renderCell: (params: GridRenderCellParams<CatalogItemRowData<TItem>>) => {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Typography variant="body2">{params.row.catalogGroup?.icon ?? '-'}</Typography>
            </Box>
          );
        },
      },
      {
        field: 'icon',
        headerName: 'Icon Override',
        width: 200,
        renderCell: (params: GridRenderCellParams<CatalogItemRowData<TItem>>) => {
          const pendingIcon = modifiedRows[params.row.code]?.icon;
          const currentIcon = pendingIcon !== undefined ? pendingIcon : params.value;

          return (
            <Autocomplete
              size="small"
              options={icons}
              value={currentIcon ?? null}
              onChange={(_event, newValue: AccommodationService.Icon | null) => {
                setModifiedRows((prev) => ({
                  ...prev,
                  [params.row.code]: { ...prev[params.row.code], icon: newValue } as NewItemChanges<TUpdatePayload>,
                }));
              }}
              getOptionLabel={(option) => option}
              isOptionEqualToValue={(option, value) => option === value}
              renderInput={(inputParams) => <TextField {...inputParams} variant="outlined" placeholder="Select icon" />}
              fullWidth
              sx={{ minWidth: 180 }}
            />
          );
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 200,
        sortable: false,
        renderCell: (params: GridRenderCellParams<CatalogItemRowData<TItem>>) => {
          const hasChanges = Boolean(modifiedRows[params.row.code]);
          const isSaving = updateMutation.isPending || createMutation.isPending;
          const isDeleting = deletingCodes.has(params.row.code);
          const isNewRow = params.row.code.startsWith(NEW_ROW_CODE_PREFIX);
          const isDraft = params.row.status === 'draft';
          const isApproving = approvingCodes.has(params.row.code);
          const isDiscarding = discardingCodes.has(params.row.code);

          // Draft items have different actions: approve, discard, merge
          if (isDraft && !isNewRow) {
            return (
              <Stack direction="row" spacing={0.5}>
                <Tooltip title={`Approve ${config.itemName.toLowerCase()}`}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleApprove(params.row)}
                      disabled={isApproving || isDiscarding}
                      sx={{ color: green[600] }}
                    >
                      {isApproving ? <CircularProgress size={18} /> : <CheckIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={`Discard ${config.itemName.toLowerCase()}`}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleDiscard(params.row.code)}
                      disabled={isApproving || isDiscarding}
                      sx={{ color: red[600] }}
                    >
                      {isDiscarding ? <CircularProgress size={18} /> : <CloseIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={`Merge into existing ${config.itemName.toLowerCase()}`}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenMergeModal(params.row)}
                      disabled={isApproving || isDiscarding}
                      sx={{ color: blue[600] }}
                    >
                      <MergeTypeIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            );
          }

          // Approved items and new rows have the standard actions: save, clear, manage supplier names, merge, delete
          return (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title={isNewRow ? `Create ${config.itemName.toLowerCase()}` : 'Save changes'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleSave(params.row)}
                    disabled={!hasChanges || isSaving || isDeleting}
                    sx={{ color: hasChanges && !isSaving ? blue[600] : undefined }}
                  >
                    {isSaving ? <CircularProgress size={18} /> : <SaveIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={isNewRow ? 'Remove row' : 'Clear changes'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleClearChanges(params.row.code)}
                    disabled={!hasChanges || isSaving || isDeleting}
                  >
                    <ClearIcon />
                  </IconButton>
                </span>
              </Tooltip>
              {!isNewRow && (
                <Tooltip title="Manage Supplier Names">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenManageSupplierNames(params.row)}
                      disabled={isSaving || isDeleting}
                      sx={{ color: blue[600] }}
                    >
                      <SettingsIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title={`Merge into existing ${config.itemName.toLowerCase()}`}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenMergeModal(params.row)}
                    disabled={isSaving || isDeleting}
                    sx={{ color: blue[600] }}
                  >
                    <MergeTypeIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={isNewRow ? 'Remove row' : `Delete ${config.itemName.toLowerCase()}`}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteClick(params.row)}
                    disabled={isSaving || isDeleting}
                    sx={{ color: red[600] }}
                  >
                    {isDeleting ? <CircularProgress size={18} /> : <DeleteIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [
      catalogGroups,
      icons,
      handleSave,
      handleClearChanges,
      handleDeleteClick,
      handleApprove,
      handleDiscard,
      handleOpenMergeModal,
      handleOpenManageSupplierNames,
      updateMutation.isPending,
      createMutation.isPending,
      deletingCodes,
      approvingCodes,
      discardingCodes,
      modifiedRows,
      config,
    ],
  );

  // Combine local new rows with API rows (local rows first to appear at top)
  const allRows = useMemo(() => [...localRows, ...items], [localRows, items]);

  const catalogGroupOptions: Array<CatalogGroupOption & { id: string }> = catalogGroups.map((group) => ({
    id: group.id,
    name: group.name,
    code: group.code,
  }));

  const selectedFilterGroup = catalogGroupOptions.find((opt) => opt.id === filters.filterCatalogGroupId) ?? null;

  if (error) {
    return (
      <Alert severity="error">
        Error loading {config.itemNamePlural.toLowerCase()}: {error instanceof Error ? error.message : 'Unknown error'}
      </Alert>
    );
  }

  return (
    <Stack direction="column" spacing={2} sx={{ pt: 2 }}>
      <Box component="form" onSubmit={handleSearchSubmit}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            label="Search by name"
            value={filters.searchName}
            onChange={(e) => setSearchName(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <Autocomplete
            size="small"
            options={catalogGroupOptions}
            value={selectedFilterGroup}
            onChange={(_event, newValue) => {
              setFilterCatalogGroupId(newValue?.id);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label="Filter by Catalog Group" variant="outlined" />}
            sx={{ minWidth: 250 }}
          />
          <TextField
            size="small"
            select
            label="Status"
            value={filters.filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value as typeof filters.filterStatus);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
          </TextField>
          <Button variant="contained" type="submit">
            Search
          </Button>
          <Button
            variant="text"
            onClick={() => {
              clearFilters();
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
          >
            Clear
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          {filters.filterStatus === 'approved' && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNew}>
              Add New
            </Button>
          )}
          <Button variant="outlined" startIcon={<SettingsIcon />} onClick={() => setIsManageGroupsModalOpen(true)}>
            Manage Groups
          </Button>
        </Stack>
      </Box>

      <Stack direction="column" spacing={2} alignItems="left">
        <Typography variant="body2" color="textSecondary">
          {filters.filterStatus === 'approved'
            ? `Edit fields directly. Changes are tracked per row - click Save to persist changes for that ${config.itemName.toLowerCase()}.`
            : `Draft ${config.itemNamePlural.toLowerCase()} can be approved, discarded, or merged into existing approved ${config.itemNamePlural.toLowerCase()}.`}
        </Typography>
        <Link
          href="https://master--6058033943ab64002196aa55.chromatic.com/?path=/story/luxkit-icons--line-icons"
          target="_blank"
          rel="noopener noreferrer"
        >
          View icon reference
        </Link>
      </Stack>

      <DataGrid
        columns={columns}
        rows={allRows}
        getRowId={(row) => row.code}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        rowCount={totalCount + localRows.length}
        loading={isLoading || (isFetching && isPlaceholderData)}
        pageSizeOptions={[PAGE_SIZE]}
        autoHeight
        disableRowSelectionOnClick
        getRowHeight={() => 'auto'}
        sx={{
          '& .MuiDataGrid-cell': {
            py: 1,
          },
        }}
      />

      <ManageCatalogGroupsModal
        open={isManageGroupsModalOpen}
        onClose={() => setIsManageGroupsModalOpen(false)}
        type={config.type}
      />

      <MergeCatalogItemModal
        open={isMergeModalOpen}
        onClose={() => {
          setIsMergeModalOpen(false);
          setSelectedDraftForMerge(null);
        }}
        draftItem={selectedDraftForMerge}
        onMerge={handleMerge}
        isMerging={mergeMutation.isPending}
        config={config}
      />

      {selectedItemForSupplierNames && (
        <ManageSupplierNamesModal
          open={isManageSupplierNamesModalOpen}
          onClose={() => {
            setIsManageSupplierNamesModalOpen(false);
            setSelectedItemForSupplierNames(null);
          }}
          itemCode={selectedItemForSupplierNames.code}
          itemName={selectedItemForSupplierNames.name}
          currentAliases={
            (modifiedRows[selectedItemForSupplierNames.code] as any)?.aliases ??
            selectedItemForSupplierNames.aliases ??
            []
          }
          type={config.type}
          onSave={handleSaveSupplierNames}
        />
      )}
    </Stack>
  );
}
